import React from 'react';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface UsageChartData {
  time: string;
  usage: number;
  dailyCost: number;
  cumulativeCost: number;
}

interface UsageComposedChartProps {
  data: UsageChartData[];
  title?: string;
  dateRange?: string;
  onPrevious?: () => void;
  onNext?: () => void;
  canGoPrevious?: boolean;
  canGoNext?: boolean;
  onReturnToPresent?: () => void;
  isAtPresent?: boolean;
}

const UsageComposedChart: React.FC<UsageComposedChartProps> = ({ 
  data, 
  title = "Usage",
  dateRange,
  onPrevious,
  onNext,
  canGoPrevious = true,
  canGoNext = true,
  onReturnToPresent,
  isAtPresent = true
}) => {
  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const dailyCost = payload[0]?.value || 0;
      const cumulativeCost = payload[1]?.value || 0;
      const usage = payload[0]?.payload?.usage || 0;

      return (
        <div className="dark:bg-surface bg-light-surface border dark:border-border-color border-light-border-color rounded-lg shadow-lg p-3">
          <p className="font-semibold dark:text-text-primary text-light-text-primary mb-2">{label}</p>
          <div className="space-y-1 text-sm">
            <p className="dark:text-text-secondary text-light-text-secondary">
              Usage: <span className="font-medium dark:text-text-primary text-light-text-primary">{usage.toLocaleString()}</span>
            </p>
            <p className="dark:text-text-secondary text-light-text-secondary">
              Daily Cost: <span className="font-medium text-primary">€{dailyCost.toFixed(2)}</span>
            </p>
            <p className="dark:text-text-secondary text-light-text-secondary">
              Cumulative: <span className="font-medium text-green-500">€{cumulativeCost.toFixed(2)}</span>
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="flex flex-col items-center w-full h-full">
      <p className="text-m font-semibold dark:text-text-primary text-light-text-primary mb-3">{title}</p>
      
      <ResponsiveContainer width="100%" height={200}>
        <ComposedChart
          data={data}
          margin={{ top: 10, bottom: 0 }}
        >
          <CartesianGrid 
            strokeDasharray="3 3" 
            stroke="var(--chart-grid-color, #374151)"
            opacity={0.3}
          />
          <XAxis 
            dataKey="time" 
            tick={false}
            axisLine={{ stroke: 'var(--chart-axis-color, #374151)' }}
          />
          <YAxis 
            yAxisId="left"
            tick={{ fill: 'var(--chart-text-color, #9CA3AF)', fontSize: 11 }}
            axisLine={{ stroke: 'var(--chart-axis-color, #374151)' }}
            label={{ 
              value: '€', 
              angle: -90, 
              position: 'insideLeft',
              style: { fill: 'var(--chart-text-color, #9CA3AF)', fontSize: 11 }
            }}
          />
          <YAxis 
            yAxisId="right"
            orientation="right"
            tick={{ fill: 'var(--chart-text-color, #9CA3AF)', fontSize: 11 }}
            axisLine={{ stroke: 'var(--chart-axis-color, #374151)' }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend 
            wrapperStyle={{ fontSize: '12px', paddingTop: '10px', textAlign: 'center' }}
            iconType="circle"
            align="center"
          />
          <Bar 
            yAxisId="left"
            dataKey="dailyCost" 
            fill="#6366F1" 
            name="Daily Cost (€)"
            radius={[4, 4, 0, 0]}
            maxBarSize={40}
          />
          <Line 
            yAxisId="right"
            type="monotone" 
            dataKey="cumulativeCost" 
            stroke="#10B981" 
            strokeWidth={2.5}
            name="Cumulative (€)"
            dot={{ fill: '#10B981', r: 4 }}
            activeDot={{ r: 6 }}
          />
        </ComposedChart>
      </ResponsiveContainer>

      {/* Date Navigation */}
      <div className="flex items-center justify-center gap-4 mt-3 w-full">
        <button
          onClick={onPrevious}
          disabled={!canGoPrevious}
          className={`p-1 rounded transition-colors ${
            !canGoPrevious
              ? 'dark:text-text-secondary/30 text-light-text-secondary/30 cursor-not-allowed'
              : 'dark:text-text-secondary text-light-text-secondary dark:hover:bg-gray-700 hover:bg-gray-100 dark:hover:text-text-primary hover:text-light-text-primary'
          }`}
          aria-label="Previous week"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        
        <div className="text-center min-w-[130px]">
          <p className="text-xs dark:text-text-secondary text-light-text-secondary">
            {dateRange || 'Current Week'}
          </p>
          {!isAtPresent && onReturnToPresent && (
            <button
              onClick={onReturnToPresent}
              className="text-[10px] text-primary hover:text-primary-hover underline mt-0.5"
            >
              Today
            </button>
          )}
        </div>
        
        <button
          onClick={onNext}
          disabled={!canGoNext}
          className={`p-1 rounded transition-colors ${
            !canGoNext
              ? 'dark:text-text-secondary/30 text-light-text-secondary/30 cursor-not-allowed'
              : 'dark:text-text-secondary text-light-text-secondary dark:hover:bg-gray-700 hover:bg-gray-100 dark:hover:text-text-primary hover:text-light-text-primary'
          }`}
          aria-label="Next week"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default UsageComposedChart;
